/* eslint-disable no-param-reassign, prefer-destructuring */
import { type PayloadAction, type Slice, createSlice } from "@reduxjs/toolkit"
import type { WritableDraft } from "immer"
import eq from "lodash-es/eq"
import every from "lodash-es/every"
import filter from "lodash-es/filter"
import findLast from "lodash-es/findLast"
import isEmpty from "lodash-es/isEmpty"
import isNull from "lodash-es/isNull"
import isNumber from "lodash-es/isNumber"
import last from "lodash-es/last"
import map from "lodash-es/map"
import slice from "lodash-es/slice"

import {
    ECoachingChatConversationErrorVariant as EConversationError,
    ECoachingChatConversationStatus as EConversationStatus,
    type TCoachingChatConversation as TConversation,
    type TCoachingChatConversationError as TConversationError,
    type TCoachingChatConversationErrorPayload as TConversationErrorPayload,
    type TCoachingChatFormError as TFormError,
    type TCoachingChatConversationMessage as TMessage,
    type TCoachingChatSeedQuestionFormField as TQuestionFormField,
    type TCoachingChatSummaryCard as TSummaryCard
} from "../types"
import { coachingChatUiUtils as uiUtils } from "../utils"

const {
    storeUtils: { mergeMessages }
} = uiUtils

const COACHING_CHATS_REDUCER_NAME = "coachingChats"

type TFormState = {
    questions: TQuestionFormField[]
    isFilled: boolean
    isFilling: boolean
    isSubmitted: boolean
    error: TFormError | null
}

type TConversationState = {
    id: number | null
    messages: TMessage[]
    status: EConversationStatus
    summary: TSummaryCard[]
    error: TConversationError | null
    isMatrixFeedbackCompleted: boolean
    isInputDimmed: boolean
    isInputDisabled: boolean
    inputValueRaw: string
}

type TChatState = {
    form: TFormState
    conversation: TConversationState
    messagesIds: string[]
}

const defaultState: TChatState = {
    form: {
        questions: [],
        isFilling: false,
        isFilled: false,
        isSubmitted: false,
        error: { variant: null, moderationId: null, message: null }
    },
    conversation: {
        id: null,
        messages: [],
        status: EConversationStatus.NotAvailable,
        summary: [],
        error: { variant: null, payload: { messageId: null, message: null, moderationId: null } },
        isMatrixFeedbackCompleted: false,
        isInputDisabled: true,
        isInputDimmed: true,
        inputValueRaw: String("")
    },
    messagesIds: []
}

type TChatsStore = {
    chats: Record<number, TChatState>
}

const initialState: TChatsStore = {
    chats: {}
}

const coachingChatSlice: Slice<TChatsStore> = createSlice({
    name: COACHING_CHATS_REDUCER_NAME,
    initialState,
    reducers: {
        initChat(
            { chats }: WritableDraft<TChatsStore>,
            { payload: { entityId } }: PayloadAction<{ entityId: number }>
        ): void {
            if (!chats?.[entityId]) chats[entityId] = structuredClone(defaultState)
        },
        clearChat(
            { chats }: WritableDraft<TChatsStore>,
            { payload: { entityId } }: PayloadAction<{ entityId: number }>
        ): void {
            if (chats[entityId]) delete chats[entityId]
        },

        // ----- form -----
        loadSeeds(
            { chats }: WritableDraft<TChatsStore>,
            { payload: { entityId, questions } }: PayloadAction<{ entityId: number; questions: TQuestionFormField[] }>
        ): void {
            chats[entityId].form = {
                questions,
                isSubmitted: false,
                isFilling: false,
                isFilled: every<TQuestionFormField>(questions, (q: TQuestionFormField): boolean => !isEmpty(q.answer)),
                error: { variant: null, moderationId: null, message: null }
            }
        },
        populateSeed(
            { chats }: WritableDraft<TChatsStore>,
            {
                payload: { entityId, question }
            }: PayloadAction<{ entityId: number; question: Pick<TQuestionFormField, "id" | "answer"> }>
        ): void {
            const { form }: WritableDraft<TChatState> = chats[entityId]

            chats[entityId].form.questions = map<TQuestionFormField, TQuestionFormField>(
                form.questions,
                (q: TQuestionFormField): TQuestionFormField =>
                    eq(q.id, question.id) ? { ...q, answer: question.answer } : q
            )
            chats[entityId].form.isFilled = every<TQuestionFormField>(
                form.questions,
                (q: TQuestionFormField): boolean => !isEmpty(q.answer)
            )
        },
        submitForm(
            { chats }: WritableDraft<TChatsStore>,
            { payload: { entityId } }: PayloadAction<{ entityId: number }>
        ): void {
            if (!chats[entityId].form.isSubmitted) chats[entityId].form.isSubmitted = true
        },
        setFormFilling(
            { chats }: WritableDraft<TChatsStore>,
            { payload: { entityId, value } }: PayloadAction<{ entityId: number; value?: boolean }>
        ): void {
            if (!chats[entityId].form.isSubmitted) chats[entityId].form.isFilling = value
        },
        triggerFormError(
            { chats }: WritableDraft<TChatsStore>,
            { payload: { entityId, error } }: PayloadAction<{ entityId: number; error: TFormError }>
        ): void {
            chats[entityId].form.error = error
        },
        clearFormError(
            { chats }: WritableDraft<TChatsStore>,
            { payload: { entityId } }: PayloadAction<{ entityId: number }>
        ): void {
            chats[entityId].form.error = defaultState.form.error
        },

        // ----- conversation -----
        setConversationId(
            { chats }: WritableDraft<TChatsStore>,
            { payload: { entityId, conversationId } }: PayloadAction<{ entityId: number; conversationId: number }>
        ): void {
            if (isNull(chats[entityId].conversation.id)) chats[entityId].conversation.id = conversationId
        },
        loadConversation(
            { chats }: WritableDraft<TChatsStore>,
            {
                payload: { entityId, conversation, status }
            }: PayloadAction<{
                entityId: number
                conversation: TConversation
                status?: EConversationStatus
            }>
        ): void {
            chats[entityId].conversation = {
                id: conversation.id,
                messages: conversation.messages,
                summary: conversation.summary,
                status: status ?? conversation.status,
                error: { variant: null, payload: { messageId: null, moderationId: null, message: null } },
                isMatrixFeedbackCompleted: conversation.isMatrixFeedbackCompleted,
                isInputDisabled: true,
                isInputDimmed: true,
                inputValueRaw: String("")
            }

            chats[entityId].messagesIds = isEmpty(conversation.messages)
                ? []
                : map<TMessage, string>(conversation.messages, (m: TMessage): string => m.clientMessageId)
        },
        /**
         * @deprecated considered as legacy; as messages are now renderer by clientMessageIdOnly, no need to make a merge with sort;
         * later on it might be needed to explicitly sort messages by their ids, but for this purpose createEntityAdapter is better;
         * leaving this code as a reminder for this
         */
        populateMessages(
            // is different from addMessages as it sorts the messages by their ids
            { chats }: WritableDraft<TChatsStore>,
            { payload: { entityId, messages } }: PayloadAction<{ entityId: number; messages: TMessage[] }>
        ): void {
            if (!chats[entityId]) return

            const { conversation }: TChatState = chats[entityId]

            conversation.messages = mergeMessages(conversation.messages, messages)
        },
        markConversationInput(
            { chats }: WritableDraft<TChatsStore>,
            {
                payload: { entityId, isDisabled, isDimmed }
            }: PayloadAction<{ entityId: number; isDisabled: boolean; isDimmed: boolean }>
        ): void {
            if (!chats[entityId]) return

            chats[entityId].conversation.isInputDimmed = isDimmed
            chats[entityId].conversation.isInputDisabled = isDisabled
        },
        updateLastMessage(
            { chats }: WritableDraft<TChatsStore>,
            { payload: { entityId, message } }: PayloadAction<{ entityId: number; message: TMessage }>
        ): void {
            if (!chats[entityId]) return

            const { conversation }: TChatState = chats[entityId]

            const lastMessage: TMessage = last<TMessage>(conversation.messages)

            conversation.messages[conversation.messages.length - 1] = {
                ...lastMessage,
                ...message
            }
        },
        populateSummary(
            { chats }: WritableDraft<TChatsStore>,
            { payload: { entityId, summary } }: PayloadAction<{ entityId: number; summary: TSummaryCard[] }>
        ): void {
            if (!chats[entityId]) return

            chats[entityId].conversation.summary = summary
        },
        addMessage(
            { chats }: WritableDraft<TChatsStore>,
            {
                payload: { entityId, message }
            }: PayloadAction<{
                entityId: number
                message: TMessage
            }>
        ): void {
            if (!chats[entityId]) return

            chats[entityId].conversation.messages.push(message)
            chats[entityId].messagesIds.push(message.clientMessageId)
        },
        replaceTempMessage(
            { chats }: WritableDraft<TChatsStore>,
            {
                payload: { entityId, messageId, clientMessageId }
            }: PayloadAction<{ entityId: number; messageId: number; clientMessageId: string }>
        ): void {
            chats[entityId].conversation.messages = map<TMessage, TMessage>(
                chats[entityId].conversation.messages,
                (m: TMessage): TMessage => (eq(m.clientMessageId, clientMessageId) ? { ...m, id: messageId } : m)
            )
        },
        removeMessagesRight(
            { chats }: WritableDraft<TChatsStore>,
            { payload: { entityId, count } }: PayloadAction<{ entityId: number; count?: number }>
        ): void {
            chats[entityId].conversation.messages = slice<TMessage>(
                chats[entityId].conversation.messages,
                0,
                isNumber(count) ? -count : -1
            )
            chats[entityId].messagesIds = slice<string>(chats[entityId].messagesIds, 0, isNumber(count) ? -count : -1)
        },
        removeMessageByClientId(
            { chats }: WritableDraft<TChatsStore>,
            { payload: { entityId, id } }: PayloadAction<{ entityId: number; id: string }>
        ): void {
            chats[entityId].conversation.messages = filter<TMessage>(
                chats[entityId].conversation.messages,
                (m: TMessage): boolean => !eq(m.clientMessageId, id)
            )
            chats[entityId].messagesIds = filter<string>(
                chats[entityId].messagesIds,
                (msgId: string): boolean => !eq(msgId, id)
            )
        },
        triggerConversationError(
            { chats }: WritableDraft<TChatsStore>,
            {
                payload: { entityId, variant, data }
            }: PayloadAction<{ entityId: number; variant: EConversationError; data?: TConversationErrorPayload }>
        ): void {
            if (!chats[entityId]) return

            const { conversation }: TChatState = chats[entityId]

            conversation.error = { variant, payload: data ?? null }

            if (eq(variant, EConversationError.Violation))
                chats[entityId].conversation.inputValueRaw = findLast<TMessage>(
                    conversation.messages,
                    (m: TMessage): boolean => m.author === "user"
                ).content

            if (eq(variant, EConversationError.AiStuck) && conversation.messages.length === 1)
                conversation.messages = slice<TMessage>(conversation.messages, 0, -1)
        },
        clearConversationError(
            { chats }: WritableDraft<TChatsStore>,
            { payload: { entityId } }: PayloadAction<{ entityId: number }>
        ): void {
            chats[entityId].conversation.error = defaultState.conversation.error
        },
        changeStatus(
            { chats }: WritableDraft<TChatsStore>,
            { payload: { entityId, status } }: PayloadAction<{ entityId: number; status: EConversationStatus }>
        ): void {
            chats[entityId].conversation.status = status
        },
        setConversationInputValueRaw(
            { chats }: WritableDraft<TChatsStore>,
            { payload: { entityId, value } }: PayloadAction<{ entityId: number; value: string }>
        ): void {
            if (!chats[entityId]) return

            chats[entityId].conversation.inputValueRaw = value
        },
        completeMatrixFeedback(
            { chats }: WritableDraft<TChatsStore>,
            { payload: { entityId } }: PayloadAction<{ entityId: number }>
        ): void {
            chats[entityId].conversation.isMatrixFeedbackCompleted = true
        }
    }
})

const { actions: coachingChatActions, reducer: coachingChatReducer }: Slice<TChatsStore> = coachingChatSlice

export { coachingChatActions, coachingChatReducer, COACHING_CHATS_REDUCER_NAME }
export type {
    TChatState as TCoachingChatState,
    TFormState as TCoachingChatFormState,
    TConversationState as TCoachingChatConversationState
}
